// react-router-dom components
import { Link } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

import { useTranslation } from "react-i18next";
import { useLocalizedPath } from "../../hooks/useLocalizedNavigate";

import PointieBox from "../components/PointieBox";
import PointieTypography from "../components/PointieTypography";
import RssSubscribeButton from "../blog/RssSubscribeButton";

const MENU_I18N = {
  offers: "footer.menus.offers",
  blog: "footer.menus.blog",
  "app store": "footer.menus.appStore",
  "help & contact": "footer.menus.helpContact",
  legal: "footer.menus.legal",
};

const ITEM_I18N = {
  "On the Fly": "footer.items.onTheFly",
  Blog: "footer.items.blog",
  "Chrome extension": "footer.items.chromeExtension",
  "contact us": "footer.items.contactUs",
  FAQ: "footer.items.faq",
  "terms of service": "footer.items.termsOfService",
  "privacy policy": "footer.items.privacyPolicy",
};

function DefaultFooter({ content }) {
  const { t } = useTranslation("nav");
  const lp = useLocalizedPath();
  const { brand, socials, menus } = content;
  const year = new Date().getFullYear();

  return (
    <PointieBox component="footer">
      <Container>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3} sx={{ ml: "auto", mb: 1 }}>
            <PointieBox>
              <Link to={lp(brand.route)}>
                <PointieBox
                  component="img"
                  src={brand.image}
                  alt={t("footer.brand")}
                  maxWidth="2rem"
                  mb={1}
                />
              </Link>
              <PointieTypography variant="h6" component="h2">
                {t("footer.brand")}
              </PointieTypography>
            </PointieBox>
            <PointieBox display="flex" alignItems="center" mt={1.5}>
              {socials.map(({ icon, link }) => (
                <PointieTypography
                  key={link}
                  component="a"
                  href={link}
                  target="_blank"
                  rel="noreferrer"
                  variant="h5"
                  color="dark"
                  opacity={0.8}
                  mr={2.5}
                  aria-label={
                    link.includes("facebook")
                      ? "Facebook"
                      : link.includes("instagram")
                      ? "Instagram"
                      : link.includes("youtube")
                      ? "YouTube"
                      : link.includes("buymeacoffee")
                      ? "Buy Me a Coffee"
                      : "Social link"
                  }
                >
                  {icon}
                </PointieTypography>
              ))}
              <RssSubscribeButton variant="compact" trackingLocation="footer" />
            </PointieBox>
          </Grid>
          {menus.map(({ name: title, items }) => (
            <Grid key={title} item xs={6} md={2} sx={{ mb: 1 }}>
              <PointieTypography
                display="block"
                variant="button"
                fontWeight="bold"
                textTransform="capitalize"
                mb={0.5}
              >
                {MENU_I18N[title] ? t(MENU_I18N[title]) : title}
              </PointieTypography>
              <PointieBox component="ul" p={0} m={0} sx={{ listStyle: "none" }}>
                {items.map(({ name, route, href }) => {
                  const isInternal = href && href.startsWith("/");
                  return (
                    <PointieBox key={name} component="li" p={0} m={0} lineHeight={1.25}>
                      {isInternal ? (
                        <PointieTypography
                          component={Link}
                          to={lp(href)}
                          variant="button"
                          fontWeight="regular"
                          textTransform="capitalize"
                        >
                          {ITEM_I18N[name] ? t(ITEM_I18N[name]) : name}
                        </PointieTypography>
                      ) : href ? (
                        <PointieTypography
                          component="a"
                          href={href}
                          rel="noreferrer"
                          variant="button"
                          fontWeight="regular"
                          textTransform="capitalize"
                        >
                          {ITEM_I18N[name] ? t(ITEM_I18N[name]) : name}
                        </PointieTypography>
                      ) : (
                        <PointieTypography
                          component={Link}
                          to={lp(route)}
                          variant="button"
                          fontWeight="regular"
                          textTransform="capitalize"
                          sx={{ display: "inline-block", py: 0.75, minHeight: 24 }}
                        >
                          {ITEM_I18N[name] ? t(ITEM_I18N[name]) : name}
                        </PointieTypography>
                      )}
                    </PointieBox>
                  );
                })}
              </PointieBox>
            </Grid>
          ))}
          <Grid item xs={12} sx={{ textAlign: "center", my: 1.5 }}>
            <PointieTypography variant="button" fontWeight="regular">
              <span dangerouslySetInnerHTML={{ __html: t("footer.copyright", { year }) }} />
            </PointieTypography>
          </Grid>
        </Grid>
      </Container>
    </PointieBox>
  );
}

// Typechecking props for the DefaultFooter
DefaultFooter.propTypes = {
  content: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.object, PropTypes.array])).isRequired,
};

export default DefaultFooter;
